import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
// RGL 2.x ships a modern hook-based grid at the root and the CLASSIC component (cols /
// rowHeight / layout / WidthProvider — the API this dashboard uses) at `/legacy`. The
// legacy build has no findDOMNode, so it's React-19-safe. It uses the native
// ResizeObserver, which the test setup polyfills for jsdom (ADR 0124).
import ReactGridLayout, { WidthProvider, type Layout, type LegacyReactGridLayoutProps } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { SC } from "./studioChrome";
import { sanitizeLayout, type WidgetLayout } from "./studioWorkspaces";

// The per-tab widget canvas (ADR 0124). Renders the workspace's placed widgets in a
// grid below the fixed monitor spine. In EDIT mode each widget gets a drag handle +
// remove-to-toolbox control and can be dragged/resized; the toolbox (left rail) lists
// widgets not placed on any tab. In VIEW mode it's locked and each card is pinned to the
// height its content needs — no fixed-cell inner scrollbars. Widgets stay in-render JSX
// (their `node`), so console wiring + the existing tests are preserved.

// WidthProvider's inferred return type defaults to the modern (no-`cols`) props; the
// wrapped legacy component takes the classic props, so type the wrapper explicitly.
const Grid = WidthProvider(ReactGridLayout) as ComponentType<Omit<LegacyReactGridLayoutProps, "width">>;

// Grid geometry (kept in sync with the <Grid> props below): an item `h` rows tall is
// `h*ROW_H + (h-1)*MARGIN_Y` px. rowsForPx sizes a card UP to fit its content; maxRowsForPx
// caps a card DOWN to the visible canvas so a huge list scrolls inside its card, not the page.
const ROW_H = 26;
const MARGIN_Y = 8;
const rowsForPx = (px: number) => Math.max(1, Math.ceil((px + MARGIN_Y) / (ROW_H + MARGIN_Y)));
const maxRowsForPx = (px: number) => Math.max(1, Math.floor((px + MARGIN_Y) / (ROW_H + MARGIN_Y)));

// Scrollbars are hidden across the whole card area (both a capped card's own overflow and
// any list widget's internal scroller) — scrolling still works, there's just no visible
// bar. Injected once; scoped to .sc-card-area so the monitor spine + chrome are untouched.
const CARD_AREA_CSS =
  ".sc-card-area,.sc-card-area *{scrollbar-width:none;-ms-overflow-style:none}" +
  ".sc-card-area::-webkit-scrollbar,.sc-card-area ::-webkit-scrollbar{width:0;height:0;display:none}";

export interface WidgetDef {
  id: string;
  title: string;
  node: ReactNode;
  minW?: number;
  minH?: number;
  /** For a toolbox entry: the tab it currently sits on (undefined = unplaced). Adding it
   *  here MOVES it (singletons, ADR 0124/0125). */
  location?: string;
}

// WidgetBody wraps a widget's content so we can measure its NATURAL height (the inner
// block sizes to content regardless of the cell), which drives the card's row count in
// VIEW mode. The outer div fills the cell and scrolls only when the card was capped to
// the canvas height (bar hidden via .sc-card-area). In EDIT mode there's no measurement —
// the operator's saved sizes stand and the cell scrolls as before.
function WidgetBody({
  id,
  measure,
  editMode,
  onHeight,
  children,
}: {
  id: string;
  measure: boolean;
  editMode: boolean;
  onHeight: (id: string, px: number) => void;
  children: ReactNode;
}) {
  const inner = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!measure) return;
    const el = inner.current;
    if (!el) return;
    const report = () => onHeight(id, el.offsetHeight);
    report();
    let ro: ResizeObserver | undefined;
    try {
      ro = new ResizeObserver(report);
      ro.observe(el);
    } catch {
      /* no ResizeObserver (old env) — the initial report still seeds a sensible height */
    }
    return () => ro?.disconnect();
  }, [id, measure, onHeight]);
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: editMode ? 6 : 0 }}>
      <div ref={inner}>{children}</div>
    </div>
  );
}

export function WorkspaceCanvas({
  placed,
  toolbox,
  layout,
  editMode,
  onLayoutChange,
  onAdd,
  onRemove,
}: {
  placed: WidgetDef[];
  toolbox: WidgetDef[];
  layout: WidgetLayout[];
  editMode: boolean;
  onLayoutChange: (l: WidgetLayout[]) => void;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  // Narrow-viewport fallback (ADR 0126): stack to a single column below the operator
  // breakpoint (operator consoles are desktop/tablet-first). We DON'T persist the
  // re-flowed layout — layout writes are gated to edit mode + normal width — so a saved
  // wide arrangement survives a resize.
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1024) < 700);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const cols = narrow ? 1 : 12;

  // VIEW mode pins each card to its content height (no fixed-cell scrollbars); EDIT mode
  // keeps the operator's saved sizes so drag/resize stays meaningful.
  const autoSize = !editMode && !narrow;

  // Measured natural content heights (px) per widget id, and the canvas viewport height
  // (the cap). Updated only in VIEW mode; a >1px threshold keeps the measure→layout loop
  // from oscillating.
  const [heights, setHeights] = useState<Record<string, number>>({});
  const onHeight = useCallback((id: string, px: number) => {
    setHeights((prev) => (Math.abs((prev[id] ?? -1) - px) > 1 ? { ...prev, [id]: px } : prev));
  }, []);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasH, setCanvasH] = useState(0);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const report = () => setCanvasH(el.clientHeight);
    report();
    let ro: ResizeObserver | undefined;
    try {
      ro = new ResizeObserver(report);
      ro.observe(el);
    } catch {
      /* no ResizeObserver — skip the cap; uncapped cards just let the canvas scroll */
    }
    return () => ro?.disconnect();
  }, []);

  // Derive the layout the grid renders: in VIEW mode, override each card's `h` with the
  // rows its measured content needs, capped to the canvas so a tall list scrolls inside
  // its own card. Unmeasured cards (initial paint / test env) keep their saved `h`.
  const effectiveLayout = useMemo(() => {
    if (!autoSize) return layout;
    const cap = canvasH > 0 ? maxRowsForPx(canvasH - 4) : Infinity;
    return layout.map((it) => {
      const px = heights[it.i];
      if (px == null) return it;
      const rows = Math.min(cap, rowsForPx(px));
      return rows === it.h ? it : { ...it, h: rows };
    });
  }, [autoSize, layout, heights, canvasH]);

  return (
    <div aria-label="Workspace canvas" style={{ display: "flex", gap: 8, alignItems: "stretch", height: "100%", minHeight: 0 }}>
      <style>{CARD_AREA_CSS}</style>
      {editMode && (
        <aside aria-label="Widget toolbox" style={{ flex: "none", width: 178, overflow: "auto", background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim }}>TOOLBOX</div>
          <div style={{ fontSize: 10, color: SC.dim, lineHeight: 1.4 }}>Add a widget to this tab, then drag its ⠿ handle to move and the corner to resize. A widget lives on one tab — adding it here moves it.</div>
          {toolbox.length === 0 ? (
            <div style={{ fontSize: 11, color: SC.dim2, paddingTop: 4 }}>Every widget is already on this tab.</div>
          ) : (
            toolbox.map((w) => (
              <button key={w.id} type="button" aria-label={`Add ${w.title} widget`} onClick={() => onAdd(w.id)}
                style={{ display: "flex", flexDirection: "column", gap: 1, padding: "7px 10px", borderRadius: 9, border: `1px solid ${w.location ? SC.hair3 : SC.blueEdge}`, background: w.location ? SC.chipBg : SC.blueTint, color: w.location ? SC.text : SC.blueHi, fontFamily: SC.sans, fontSize: 12, textAlign: "left", cursor: "pointer" }}>
                <span>＋ {w.title}</span>
                {w.location && <span style={{ fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.04em", color: SC.dim }}>on {w.location} — move here</span>}
              </button>
            ))
          )}
        </aside>
      )}
      <div ref={canvasRef} className="sc-card-area" style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", position: "relative" }}>
        {placed.length === 0 ? (
          <div role="note" style={{ height: "100%", minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: SC.dim, fontSize: 12, padding: 24 }}>
            {editMode ? "Empty tab — add widgets from the toolbox on the left." : "Empty tab — turn on ✎ Edit and add widgets from the toolbox."}
          </div>
        ) : (
          <Grid
            className="ws-canvas"
            cols={cols}
            rowHeight={ROW_H}
            margin={[8, MARGIN_Y]}
            layout={effectiveLayout as unknown as Layout}
            isDraggable={editMode && !narrow}
            isResizable={editMode && !narrow}
            draggableHandle=".ws-drag"
            compactType="vertical"
            onLayoutChange={(l: Layout) => {
              // Persist only real edits at normal width — never the mount pass, the narrow
              // re-flow, or a VIEW-mode auto-height (which would clobber the saved layout).
              if (editMode && !narrow) onLayoutChange(sanitizeLayout(l as unknown as WidgetLayout[]));
            }}
            style={{ minHeight: "100%" }}
          >
            {placed.map((w) => (
              <div key={w.id} style={{ display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 12, border: editMode ? `1px dashed ${SC.hair2}` : "none", background: editMode ? "rgba(255,255,255,0.02)" : "transparent" }}>
                {editMode && (
                  <div className="ws-drag" style={{ flex: "none", display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", cursor: "move", background: SC.panel2, borderBottom: `1px solid ${SC.hair}`, borderRadius: "12px 12px 0 0" }}>
                    <span aria-hidden style={{ color: SC.dim2, fontSize: 11 }}>⠿</span>
                    <span style={{ flex: 1, minWidth: 0, fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.05em", color: SC.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{w.title.toUpperCase()}</span>
                    <button type="button" aria-label={`Remove ${w.title} widget`} onMouseDown={(e) => e.stopPropagation()} onClick={() => onRemove(w.id)}
                      style={{ border: "none", background: "none", color: SC.redSoft, cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 0 }}>✕</button>
                  </div>
                )}
                <WidgetBody id={w.id} measure={autoSize} editMode={editMode} onHeight={onHeight}>{w.node}</WidgetBody>
              </div>
            ))}
          </Grid>
        )}
      </div>
    </div>
  );
}
